import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CreateUser.css';
import PageTitle from '../../components/UI/PageTitle';
import { getUserById, updateUser } from '../../api/Index';

const EditUser = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'member',
        password: '',
        emergencyContact: {
            name: '',
            contact: ''
        },
        beneficiary: {
            name: '',
            contact: ''
        },
        currentLegacyCredits: 0
    });

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Get user ID from URL query parameters
                const searchParams = new URLSearchParams(location.search);
                const userId = searchParams.get('id');
                
                if (!userId) {
                    setError('User ID not provided');
                    return;
                }

                const response = await getUserById(userId);
                console.log('User details response:', response);
                
                const userData = response.data;
                setUser(userData);
                
                // Pre-fill form data
                setFormData({
                    name: userData.name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    role: userData.role || 'member',
                    password: '',
                    emergencyContact: {
                        name: userData.emergencyContact?.name || '',
                        contact: userData.emergencyContact?.contact || ''
                    },
                    beneficiary: {
                        name: userData.beneficiary?.name || '',
                        contact: userData.beneficiary?.contact || ''
                    },
                    currentLegacyCredits: userData.currentLegacyCredits || 0
                });
                
            } catch (err) {
                setError(err.message);
                console.error('Error fetching user details:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [location.search]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData({
                ...formData,
                [parent]: {
                    ...formData[parent],
                    [child]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setError(null);
        
        try {
            const searchParams = new URLSearchParams(location.search);
            const userId = searchParams.get('id');
            
            if (!userId) {
                setError('User ID not provided');
                return;
            }

            const updateData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                role: formData.role,
                emergencyContact: formData.emergencyContact,
                beneficiary: formData.beneficiary,
                currentLegacyCredits: Number(formData.currentLegacyCredits)
            };

            // Only include password if it's provided
            if (formData.password && formData.password.trim()) {
                updateData.password = formData.password;
            }

            const response = await updateUser(userId, updateData);
            console.log('User updated successfully:', response.data);
            
            // Navigate back to users page
            navigate('/users');
        } catch (err) {
            setError(err.response?.data?.msg || err.message || 'Failed to update user');
            console.error('Error updating user:', err);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div>
                <PageTitle title='Edit User' />
                <div>Loading user details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <PageTitle title='Edit User' />
                <div style={{color: 'red'}}>Error: {error}</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div>
                <PageTitle title='Edit User' />
                <div>User not found.</div>
            </div>
        );
    }

  return (
    <div>
        <PageTitle title='Edit User' />
        <div className="create-user-container">
            <div className="form-container">
                <form className="create-user-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label className="input-label">Name *</label>
                        <input
                            type="text"
                            className="input"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter full name"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Email *</label>
                        <input
                            type="email"
                            className="input"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter email address"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Phone</label>
                        <input
                            type="tel"
                            className="input"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                        />
                    </div>

                    <div className='role-group'>
                        <label className="input-label">Role</label>
                        <select 
                            name="role" 
                            value={formData.role} 
                            onChange={handleInputChange}
                            className="input"
                        >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Password (Optional)</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                className="input"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter new password (leave empty to keep current)"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '14px'
                                }}
                            >
                                {showPassword ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Emergency Contact Name</label>
                        <input
                            type="text"
                            className="input"
                            name="emergencyContact.name"
                            value={formData.emergencyContact.name}
                            onChange={handleInputChange}
                            placeholder="Enter emergency contact name"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Emergency Contact Phone</label>
                        <input
                            type="tel"
                            className="input"
                            name="emergencyContact.contact"
                            value={formData.emergencyContact.contact}
                            onChange={handleInputChange}
                            placeholder="Enter emergency contact phone"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Beneficiary Name</label>
                        <input
                            type="text"
                            className="input"
                            name="beneficiary.name"
                            value={formData.beneficiary.name}
                            onChange={handleInputChange}
                            placeholder="Enter beneficiary name"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Beneficiary Contact</label>
                        <input
                            type="tel"
                            className="input"
                            name="beneficiary.contact"
                            value={formData.beneficiary.contact}
                            onChange={handleInputChange}
                            placeholder="Enter beneficiary contact"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Legacy Credits</label>
                        <input
                            type="number"
                            className="input"
                            name="currentLegacyCredits"
                            value={formData.currentLegacyCredits}
                            onChange={handleInputChange}
                            placeholder="Enter legacy credits"
                            min="0"
                        />
                    </div>

                    <button 
                        type="submit" 
                        className='form-button'
                        disabled={updating}
                    >
                        {updating ? 'Updating...' : 'Update User'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default EditUser;